/**
 * Cloud access for `portfolio_snapshots` <- localStorage
 * `mycryptoplanner_snapshots`. One row per ISO date (composite PK
 * user_id + date), so this table has no `id` column and upserts conflict on
 * (user_id, date). Full history is retained server-side (no 365-day cap).
 */
import type { PortfolioSnapshot } from '../../types';
import type { PortfolioSnapshotRow } from './rows';
import { supabase } from '../supabaseClient';
import { assertNoError } from './client';

const TABLE = 'portfolio_snapshots';

export function toRow(s: PortfolioSnapshot, userId: string): PortfolioSnapshotRow {
  return {
    user_id: userId,
    date: s.date,
    value: s.value,
  };
}

export function fromRow(row: PortfolioSnapshotRow): PortfolioSnapshot {
  return {
    date: row.date,
    value: Number(row.value),
  };
}

async function fetchAll(userId: string): Promise<PortfolioSnapshot[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('user_id', userId)
    .order('date', { ascending: true });
  assertNoError(error, `fetchAll(${TABLE})`);
  return (data ?? []).map(fromRow);
}

/** Upsert a single snapshot, conflict-resolved on the (user_id, date) PK. */
async function upsert(row: PortfolioSnapshotRow): Promise<void> {
  const { error } = await supabase
    .from(TABLE)
    .upsert(row as any, { onConflict: 'user_id,date' });
  assertNoError(error, `upsert(${TABLE})`);
}

/** Upsert many snapshots in one round-trip, conflict-resolved on (user_id, date). */
async function upsertMany(rows: PortfolioSnapshotRow[]): Promise<void> {
  if (rows.length === 0) return;
  const { error } = await supabase
    .from(TABLE)
    .upsert(rows as any, { onConflict: 'user_id,date' });
  assertNoError(error, `upsertMany(${TABLE})`);
}

async function removeAll(userId: string): Promise<void> {
  const { error } = await supabase.from(TABLE).delete().eq('user_id', userId);
  assertNoError(error, `removeAll(${TABLE})`);
}

/**
 * Delete the user's snapshots whose `date` is NOT in `keepDates` — the prune
 * half of a reconcile (snapshots key on date, not id). Empty `keepDates` deletes
 * all the user's snapshots (local array is empty).
 */
async function removeNotInDates(userId: string, keepDates: string[]): Promise<void> {
  let q = supabase.from(TABLE).delete().eq('user_id', userId);
  if (keepDates.length > 0) {
    const list = keepDates.map((d) => `"${d}"`).join(',');
    q = q.not('date', 'in', `(${list})`);
  }
  const { error } = await q;
  assertNoError(error, `removeNotInDates(${TABLE})`);
}

export const snapshotsTable = {
  toRow,
  fromRow,
  fetchAll,
  upsert,
  upsertMany,
  removeAll,
  removeNotInDates,
};
